import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { push } from 'connected-react-router'
import { ApiMiddlewarePayload } from '@ocean/api'
import { SessionUtils, ExpanderUtils } from '@ocean/utils'
import { AppThunk, history } from 'store'
import { createApiAction } from 'api'
import {
  UserUtils,
  EnvironmentUtils,
  LegacyUtils,
  ReferrerUtils,
  MobileNativeUtils,
  SsoUtils,
} from 'utils'
import { keepSessionAlive, logout } from 'core/session/api/sessionService'
import { ClearSessionInfoOptions } from 'core/session/types'
import authRoutes from 'modules/Auth/routing/routes'
import {
  HighlightedNotificationsUtils,
  EventNotificationsUtils,
  MaintenanceNotificationsUtils,
  BannerNotificationsUtils,
} from 'modules/Notifications/utils'
import { OnboardingUtils } from 'modules/Onboarding/utils'
import ssoRoutes from 'modules/Sso/routing/routes'
import { EndUserDeclarationUtils } from 'modules/simulatorTrainings/common/utils'
import { SessionState } from './types'

const initialState: SessionState = {
  keepAlive: {},
  checkTokenAlive: {},
  isInitialTokenAlive: false,
}

const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    refreshTokenDuration(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.keepAlive = action.payload
    },
    tokenChecked(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.checkTokenAlive = action.payload
    },
    setTokenAlive(state, action: PayloadAction<boolean>) {
      state.isInitialTokenAlive = action.payload
    },
    userLoggedOut() {
      return initialState
    },
  },
})

const { refreshTokenDuration, tokenChecked, userLoggedOut, setTokenAlive } = sessionSlice.actions

export { setTokenAlive, userLoggedOut }
export default sessionSlice.reducer

const handleKeepTokenAlive =
  (response: ApiMiddlewarePayload): AppThunk =>
  async dispatch => {
    const { error } = response
    // Empty response (error - No JSON in response)
    if (error?.status !== 200) {
      dispatch(clearSessionInfo())
    }
    dispatch(refreshTokenDuration(response))
  }

export const keepTokenAlive = () => {
  return createApiAction({
    apiCalls: {
      keepAlive: keepSessionAlive,
    },
    payloadAction: refreshTokenDuration,
    nextAction: handleKeepTokenAlive,
  })
}

const handleCheckTokenAlive =
  (response: ApiMiddlewarePayload): AppThunk =>
  async dispatch => {
    const { error } = response
    // Empty response (error - No JSON in response)
    if (error?.status === 200) {
      dispatch(setTokenAlive(true))
    } else {
      dispatch(clearSessionInfo())
    }
    dispatch(tokenChecked(response))
  }

export const checkInitialTokenAlive = () => {
  return createApiAction({
    apiCalls: {
      keepAlive: keepSessionAlive,
    },
    payloadAction: tokenChecked,
    nextAction: handleCheckTokenAlive,
  })
}

const getLoginUrl = (needReturnUrl?: boolean) => {
  const returnParamName = `returnUrl`
  if (needReturnUrl) {
    const returnUrl = ReferrerUtils.getLocationPath(history.location)
    // First enter exception
    if (returnUrl === '/') return authRoutes.login
    // Prevent add query if returnUrl already has query param (like: /auth/login?returnUrl=home)
    // Case if function called several times
    if (returnUrl.includes(returnParamName)) return returnUrl
    return `${authRoutes.login}?${returnParamName}=${returnUrl}`
  }
  return authRoutes.login
}

export const clearSessionInfo =
  (options?: ClearSessionInfoOptions): AppThunk =>
  dispatch => {
    SessionUtils.clearSession()
    sessionStorage.removeItem('DIGITAL_DELIVERY_SESSION_ID')
    UserUtils.clearUserInfo()
    EnvironmentUtils.clearEnvironmentConfiguration()
    LegacyUtils.clearStaNotifications()
    OnboardingUtils.clearOnboardingViewed()
    EndUserDeclarationUtils.clearDeclarationViewed()
    ReferrerUtils.clearReferrers()
    ExpanderUtils.clearExpanderStates()
    HighlightedNotificationsUtils.clearHighlightedNotificationsViewed()
    EventNotificationsUtils.clearEventNotificationsViewed()
    MaintenanceNotificationsUtils.clearMaintenanceNotificationsViewed()
    BannerNotificationsUtils.clearBannerNotificationsViewed()
    dispatch(userLoggedOut())

    const ssoCompanyMarker = SsoUtils.getSsoMarker()
    if (ssoCompanyMarker) {
      const ssoLogoutAddress = `${ssoRoutes.logout}?ecn=${ssoCompanyMarker}`
      dispatch(push(ssoLogoutAddress))
      return
    }

    dispatch(push(getLoginUrl(options?.needReturnUrl)))
  }

export const logoutUser = (): AppThunk => dispatch => {
  try {
    const sessionToken = SessionUtils.getSessionToken()
    if (sessionToken) {
      MobileNativeUtils.loggedOut()
      logout()
    }
  } finally {
    dispatch(clearSessionInfo())
  }
}
